import { createFridge, updateFridge, useFridgeForm } from '@gozoki/api'
import { FRIDGE_TYPES } from '@gozoki/api-types'
import { Button, IconButton, TextField, Typography } from '@mui/material'
import { skipToken, useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { useEffect } from 'react'
import { Controller } from 'react-hook-form'
import z from 'zod'
import { useToast } from '~/components/communication/Toast'
import { api } from '~/lib/tuyau'
import { getApiFieldErrors } from '~/lib/utils'
import { withRoutePermissions } from '~/utils/rbac'
import { AuthorizedButton } from '../../../components/communication/AuthorizedButton'
import { FRIDGE_ADD, FRIDGE_EDIT } from '../../../components/routes/sensors/tabs'
import LoadingPaper from '../../../components/containers/LoadingPaper'
import SelectRhf from '../../../components/inputs/SelectRhf'
import TextFieldRhf from '../../../components/inputs/TextFieldRhf'
import Page from '../../../components/Page'
import { ThemeColors } from '../../../utils/theme/colors.theme'
import { fonts } from '../../../utils/theme/fonts.theme'
import { ButtonOverrides } from '../../../utils/theme/overrides.theme'

export const Route = createFileRoute('/_authenticated/sensors/fridge/new')({
  // Écran partagé création / édition (search param `fridgeId`) : l'un ou l'autre droit y donne
  // accès, le bouton de soumission exigeant ensuite celui du mode courant.
  staticData: {
    permission: { any: [FRIDGE_ADD, FRIDGE_EDIT] },
  },
  component: AddFridgePage,
  validateSearch: z.object({
    fridgeId: z.number().optional(),
  }),
})
withRoutePermissions(Route)

// Champs dont seul le backend peut invalider la valeur (unicité, magasin inconnu).
const FRIDGE_FORM_FIELDS = ['label', 'reference', 'storeReference'] as const

function AddFridgePage() {
  const navigate = Route.useNavigate()
  const { fridgeId } = Route.useSearch()

  const updateMode = !!fridgeId

  const [Toast, showToast] = useToast()

  // If this is update mode, use isLoading to show a loading indicator before the data arrives
  const { data, isLoading } = useQuery(
    api.sensors.showFridge.queryOptions(fridgeId ? { params: { id: fridgeId } } : skipToken),
  )

  const { control, trigger, getValues, setValue, setError } = useFridgeForm()

  const { data: stores = [] } = useQuery(api.stores.fridgeStores.queryOptions({}))

  // Fill the form with initial data once the data is fetched
  useEffect(() => {
    // Set all values
    if (data) {
      setValue('reference', data.reference || '')
      setValue('label', data.label)
      setValue('description', data.description || '')
      setValue('type', data.type as (typeof FRIDGE_TYPES)[keyof typeof FRIDGE_TYPES])
      setValue('storeReference', data.store?.checkpointStoreRef || '')
      setValue('tempMinOverride', data.tempMinOverride ?? undefined)
      setValue('tempMaxOverride', data.tempMaxOverride ?? undefined)
      setValue('breachToleranceMinutesOverride', data.breachToleranceMinutesOverride ?? undefined)
      setValue(
        'inactivityThresholdMinutesOverride',
        data.inactivityThresholdMinutesOverride ?? undefined,
      )
    }
  }, [setValue, data])

  // Preserve tab parameter when navigating back
  const navigateBack = () => navigate({ search: { tab: 'fridges' }, to: '/sensors' })

  // ******************************************************************** //
  //                              MUTATIONS                               //
  // ******************************************************************** //

  // Les erreurs de validation backend (dont les conflits d'unicité) sont rattachées à leur champ ;
  // le reste part en toast générique — on ne remonte jamais le corps d'erreur brut de l'API.
  const handleMutationError = (error: unknown, fallbackMessage: string) => {
    const fieldErrors = getApiFieldErrors(error, FRIDGE_FORM_FIELDS)
    if (fieldErrors.length > 0) {
      for (const { field, message } of fieldErrors) {
        setError(field, { type: 'manual', message })
      }
      return
    }

    showToast({ message: fallbackMessage, severity: 'error' })
  }

  const { mutate: upload, isPending: isUploading } = useMutation({
    mutationFn: createFridge,
    meta: { invalidates: [api.sensors.fridges.queryKey()] },
    onSuccess: navigateBack,
    onError: (error: unknown) =>
      handleMutationError(error, 'Une erreur est survenue lors de la création du réfrigérateur'),
  })

  const { mutate: update, isPending: isUpdating } = useMutation({
    mutationFn: updateFridge,
    meta: {
      invalidates: [api.sensors.fridges.queryKey(), api.sensors.showFridge.queryKey()],
    },
    onSuccess: navigateBack,
    onError: (error: unknown) =>
      handleMutationError(
        error,
        'Une erreur est survenue lors de la modification du réfrigérateur',
      ),
  })

  // ******************************************************************** //
  //                               HANDLERS                               //
  // ******************************************************************** //

  const uploadFridge = async () => {
    const valid = await trigger() // Trigger form validation

    if (valid) {
      upload(getValues())
    }
  }

  const handleUpdateFridge = async () => {
    const valid = await trigger() // Trigger form validation

    if (valid && fridgeId) {
      update({
        id: fridgeId,
        data: getValues(),
      })
    }
  }

  return (
    <Page sx={{ maxWidth: '50vw', margin: 'auto' }}>
      <Toast />
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <IconButton onClick={navigateBack}>
          <ArrowLeft
            style={{
              color: ThemeColors.BLACK,
              height: '24px',
              width: '24px',
            }}
          />
        </IconButton>
        <Typography style={{ ...fonts.pageSubtitle, marginLeft: '16px' }} flexGrow={1}>
          {updateMode ? 'Modifier un réfrigérateur' : 'Ajouter un réfrigérateur'}
        </Typography>

        <Button
          variant="outlined"
          sx={{ marginRight: '16px', ...ButtonOverrides.blackOutlined }}
          onClick={navigateBack}
        >
          Annuler
        </Button>
        <AuthorizedButton
          permission={updateMode ? FRIDGE_EDIT : FRIDGE_ADD}
          variant="contained"
          disabled={isUpdating || isUploading}
          onClick={updateMode ? handleUpdateFridge : uploadFridge}
        >
          {updateMode ? 'Modifier' : 'Ajouter'}
        </AuthorizedButton>
      </div>
      <LoadingPaper
        sx={{ marginTop: '40px', padding: '24px', paddingBottom: '40px' }}
        loading={isLoading && updateMode}
      >
        <Typography mb="8px" style={fonts.inputHeader}>
          Référence
        </Typography>
        <TextFieldRhf
          variant="outlined"
          placeholder="Référence (optionnel)"
          color="info"
          control={control}
          trigger={trigger}
          name="reference"
          fullWidth
          required
        />
        <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
          Libellé *
        </Typography>
        <TextFieldRhf
          variant="outlined"
          placeholder="Libellé du réfrigérateur"
          color="info"
          control={control}
          trigger={trigger}
          name="label"
          fullWidth
        />
        <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
          Description
        </Typography>
        <TextFieldRhf
          variant="outlined"
          placeholder="Description (optionnel)"
          color="info"
          control={control}
          trigger={trigger}
          name="description"
          fullWidth
          multiline
          rows={3}
        />
        <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
          Type *
        </Typography>
        <SelectRhf
          control={control}
          name="type"
          options={[
            { value: FRIDGE_TYPES.FRIDGE, label: 'Réfrigérateur' },
            { value: FRIDGE_TYPES.COLD_ROOM, label: 'Chambre froide' },
          ]}
        />
        <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
          Magasin *
        </Typography>
        <SelectRhf
          control={control}
          name="storeReference"
          options={
            stores?.map((store) => ({
              value: store.checkpointStoreRef,
              label: store.label,
            })) || []
          }
        />
        <Typography mb="4px" mt="32px" style={fonts.inputHeader}>
          Seuils personnalisés
        </Typography>
        <Typography mb="8px" variant="body2" color="text.secondary">
          Laisser vide pour utiliser le défaut de la catégorie.
        </Typography>
        <Typography mb="8px" mt="16px" style={fonts.inputHeader}>
          Seuil min personnalisé (°C)
        </Typography>
        <Controller
          control={control}
          name="tempMinOverride"
          render={({ field, fieldState }) => (
            <TextField
              variant="outlined"
              type="number"
              placeholder="vide = défaut catégorie"
              color="info"
              fullWidth
              value={field.value ?? ''}
              onChange={(e) => {
                const raw = e.target.value
                if (raw === '') {
                  field.onChange(null)
                  return
                }
                const parsed = Number(raw)
                field.onChange(Number.isNaN(parsed) ? field.value : parsed)
              }}
              onBlur={() => field.onBlur()}
              error={fieldState.error !== undefined}
              helperText={fieldState.error?.message}
            />
          )}
        />
        <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
          Seuil max personnalisé (°C)
        </Typography>
        <Controller
          control={control}
          name="tempMaxOverride"
          render={({ field, fieldState }) => (
            <TextField
              variant="outlined"
              type="number"
              placeholder="vide = défaut catégorie"
              color="info"
              fullWidth
              value={field.value ?? ''}
              onChange={(e) => {
                const raw = e.target.value
                if (raw === '') {
                  field.onChange(null)
                  return
                }
                const parsed = Number(raw)
                field.onChange(Number.isNaN(parsed) ? field.value : parsed)
              }}
              onBlur={() => field.onBlur()}
              error={fieldState.error !== undefined}
              helperText={fieldState.error?.message}
            />
          )}
        />
        <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
          Tolérance de dépassement personnalisée (min)
        </Typography>
        <Controller
          control={control}
          name="breachToleranceMinutesOverride"
          render={({ field, fieldState }) => (
            <TextField
              variant="outlined"
              type="number"
              placeholder="vide = défaut catégorie"
              color="info"
              fullWidth
              value={field.value ?? ''}
              onChange={(e) => {
                const raw = e.target.value
                if (raw === '') {
                  field.onChange(null)
                  return
                }
                const parsed = Number(raw)
                field.onChange(Number.isNaN(parsed) ? field.value : parsed)
              }}
              onBlur={() => field.onBlur()}
              error={fieldState.error !== undefined}
              helperText={fieldState.error?.message}
            />
          )}
        />
        <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
          Seuil d'inactivité personnalisé (min)
        </Typography>
        <Controller
          control={control}
          name="inactivityThresholdMinutesOverride"
          render={({ field, fieldState }) => (
            <TextField
              variant="outlined"
              type="number"
              placeholder="vide = défaut catégorie"
              color="info"
              fullWidth
              value={field.value ?? ''}
              onChange={(e) => {
                const raw = e.target.value
                if (raw === '') {
                  field.onChange(null)
                  return
                }
                const parsed = Number(raw)
                field.onChange(Number.isNaN(parsed) ? field.value : parsed)
              }}
              onBlur={() => field.onBlur()}
              error={fieldState.error !== undefined}
              helperText={fieldState.error?.message}
            />
          )}
        />
      </LoadingPaper>
    </Page>
  )
}
